<template>
  <grey-tabs
    v-model="currentTab"
    :tabs="tabs"
    has-tabs-tools
    :tabs-tools-cols="1"
    has-border
    :height="35"
    :tabsClasses="['pt-6', 'pl-6']"
  >
    <v-tab-item>
      <style-dialog-style-tab
        :is-valid.sync="isStyleValid"
        :layerStyle.sync="computedLayerStyle"
        :geometry-type="geometryType"
        :symbolization-kind="symbolizationKind"
        :symbolization-type="symbolizationType"
      />
    </v-tab-item>
    <v-tab-item>
      <style-dialog-labels-tab
        :is-valid.sync="isLabelsValid"
        :attributes="attributes"
        :labelsStyle.sync="computedLayerStyle.labels"
        :geometry-type="geometryType"
        :symbolization-type="symbolizationType"
        :layer-id="layerId"
      />
    </v-tab-item>
    <template #tabsTools>
      <v-col cols="auto" class="pa-0 pt-6 text-left">
        <dots-menu
          :items="tabsToolsMenuItems"
          @exportStyle="exportStyle"
          @importStyle="importStyle"
          @setAsDefault="isSetAsDefaultLoaded = isSetAsDefaultVisible = true"
        >
          <template #[`importStyle.title`]>
            <v-list-item style="min-height: 30px">
              <file-input ref="styleImportFile" :options="styleImportOptions" @fileAdded="importStyle">
                <template #uploadContent>
                  <v-list-item-title
                    class="text-item text-left text-subtitle-2 font-weight-regular"
                    style="cursor: pointer"
                  >
                    {{ $i18n.t('menu.importStyle') }}
                  </v-list-item-title>
                </template>
              </file-input>
            </v-list-item>
          </template>
        </dots-menu>
      </v-col>
      <main-dialog
        v-if="isSetAsDefaultLoaded"
        :is-visible.sync="isSetAsDefaultVisible"
        :loading="isSetAsDefaultLoading"
        :title="$i18n.t('dialog.title.setDefaultStyle')"
        :body="$i18n.t('dialog.body.setDefaultStyleConfirmation', { name: layerName })"
        agree-text="dialog.agreeSet"
        @agree="handleSetAsDefault"
      />
    </template>
  </grey-tabs>
</template>
<script>
import { get } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';
import FileInput from '@/components/FileInput';
import GreyTabs from '@/components/GreyTabs';
import MainDialog from '@/components/MainDialog';
import StyleDialogLabelsTab from '@/components/StyleDialogLabelsTab';
import StyleDialogStyleTab from '@/components/StyleDialogStyleTab';

export default {
  name: 'StyleDialogStyleTabs',
  components: {
    DotsMenu,
    FileInput,
    GreyTabs,
    MainDialog,
    StyleDialogLabelsTab,
    StyleDialogStyleTab,
  },
  props: {
    attributes: {
      type: Array,
      default: () => {
        return [];
      },
    },
    geometryType: {
      type: String,
      required: true,
    },
    isValid: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    layerName: {
      type: String,
      required: true,
    },
    layerId: {
      type: Number,
      required: true,
    },
    layerScope: {
      type: String,
      required: true,
    },
    layerStyle: {
      type: Object,
      default: () => {
        return {};
      },
    },
    symbolizationKind: {
      type: String,
    },
    symbolizationType: {
      type: String,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    saveDefaultStyleFunction: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    isAdminUser: get('users/currentUser@is_admin'),
    computedLayerStyle: {
      get() {
        return this.layerStyle;
      },
      set(nV) {
        this.$emit('update:layerStyle', nV);
      },
    },
    styleImportOptions() {
      return {
        url: '/',
        autoProcessQueue: false,
        maxFiles: 1,
        acceptedFiles: '.style',
      };
    },
    tabs() {
      return [{ name: this.$i18n.t('dialog.style.style') }, { name: this.$i18n.t('dialog.style.labels') }];
    },
    tabsToolsMenuItems() {
      return [
        {
          name: 'exportStyle',
          disabled: !this.isValid,
          ...(this.isValid ? {} : { tooltip: this.$i18n.t('rules.completeStyleParameters') }),
        },
        {
          name: 'importStyle',
        },
        ...(this.isAdminUser && !this.isAdmin
          ? [
              {
                name: 'setAsDefault',
                disabled: !this.isValid,
                ...(this.isValid ? {} : { tooltip: this.$i18n.t('rules.completeStyleParameters') }),
              },
            ]
          : []),
      ];
    },
  },
  data: () => ({
    currentTab: 0,
    isLabelsValid: true,
    isStyleValid: false,
    isSetAsDefaultLoaded: false,
    isSetAsDefaultVisible: false,
    isSetAsDefaultLoading: false,
  }),
  methods: {
    exportStyle() {
      this.$emit('export');
    },
    importStyle(file) {
      const styleCopy = JSON.parse(JSON.stringify(this.computedLayerStyle));
      let reader = new FileReader();
      reader.addEventListener('loadend', event => {
        try {
          const text = event.target.result;
          const jsonObject = JSON.parse(text);
          this.$emit('import', jsonObject);
        } catch {
          this.$emit('import', styleCopy);
        }
      });
      reader.readAsText(file);
      this.$refs.styleImportFile.$refs.dropzone.removeAllFiles();
    },
    async handleSetAsDefault() {
      try {
        this.isSetAsDefaultLoading = true;
        await this.saveDefaultStyleFunction();
      } finally {
        this.isSetAsDefaultLoading = false;
      }
      this.isSetAsDefaultVisible = false;
    },
  },
  watch: {
    isLabelsValid(nV) {
      this.$emit('update:isValid', nV && this.isStyleValid);
    },
    isStyleValid(nV) {
      this.$emit('update:isValid', nV && this.isLabelsValid);
    },
    //Fix dla styli zapisanych w starej strukturze
    computedLayerStyle: {
      immediate: true,
      deep: true,
      handler(nV) {
        if (!nV.labels || Array.isArray(nV.labels)) {
          this.$set(this.computedLayerStyle, 'labels', {});
        }
      },
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .v-tabs {
    position: unset !important;
  }
  .v-slide-group {
    box-shadow: none !important;
  }
  .v-tab {
    border-top: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  }
}
.v-window-item {
  padding-top: 20px !important;
}
</style>
